import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { PLAN_VERSION_MISSING_ERROR, TLLBoardInfoActionType } from './ll-board.models';
import * as LLBoardActions from './ll-board.actions';
import * as LLBoardSelectors from './ll-board.selectors';
import { TPlani8State } from '../reducers';
import { Observable, of, from } from 'rxjs';
import { switchMap, map, catchError, take, finalize, exhaustMap, withLatestFrom } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LlBoardEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private store: Store<TPlani8State>,
  ) {}

  readonly #selectedPlanVersion$ = this.store.select(LLBoardSelectors.getSelectedPlanVersion);

  #onPlanVersionMissing<A>(failureAction: A): Observable<A> {
    this.appNotificationUtilClass.openDefaultErrorModal({ errorText: PLAN_VERSION_MISSING_ERROR });
    return of(failureAction);
  }

  loadPlanVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.LoadPlanVersions),
      switchMap((action: LLBoardActions.LoadPlanVersions) =>
        this.plani8DataService.getLLBoardVersions().pipe(
          map((versions) => new LLBoardActions.LoadPlanVersionsSuccess(versions, action.keepVersionId)),
          catchError(() => of(new LLBoardActions.LoadPlanVersionsFailure())),
        ),
      ),
    ),
  );

  getLLBoardList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLLBoardList),
      withLatestFrom(this.#selectedPlanVersion$),
      exhaustMap(([action, planVersion]: [LLBoardActions.GetLLBoardList, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.GetLineShiftInfoFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService.getLLBoardList({ ...action.payload, plan_version: planVersion }).pipe(
              map((res): LLBoardActions.TLLBoardActions => new LLBoardActions.GetLLBoardListSuccess(res)),
              catchError(() => of(new LLBoardActions.LoadPlanVersions(planVersion))),
              finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loaderEl)),
            ),
          ),
        );
      }),
    ),
  );

  getLineShiftInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLineShiftInfo),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.GetLineShiftInfo, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.GetLineShiftInfoFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .getLineShiftInfo(action.subjectId, action.factoryId, planVersion, action.shiftId)
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res) => new LLBoardActions.GetLineShiftInfoSuccess(res)),
                catchError(() => {
                  return of(new LLBoardActions.GetLineShiftInfoFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  addEditWorkingHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLineWorkingHours),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.AddEditLineWorkingHours, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.AddEditLineWorkingHoursFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .addEditLineWorkingHours(action.factoryId, { ...action.payload, plan_version: planVersion })
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res) => {
                  this.appNotificationUtilClass.presentToast('Edit working count successful');
                  return new LLBoardActions.GetLineShiftInfo(
                    action.subjectId,
                    action.factoryId,
                    action.payload.shift_id,
                  );
                }),
                catchError(() => {
                  return of(new LLBoardActions.AddEditLineWorkingHoursFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  resetWorkingHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.ResetLineWorkingHours),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.ResetLineWorkingHours, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.ResetLineWorkingHoursFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .resetLineWorkingHours(action.factoryId, { ...action.payload, plan_version: planVersion })
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res) => {
                  this.appNotificationUtilClass.presentToast('Reset working hours successful');
                  return new LLBoardActions.GetLineShiftInfo(
                    action.subjectId,
                    action.factoryId,
                    action.payload.shift_id,
                  );
                }),
                catchError(() => {
                  return of(new LLBoardActions.ResetLineWorkingHoursFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  addEditLineWorkForceCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLineWorkForceCount),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.AddEditLineWorkForceCount, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.AddEditLineWorkForceCountFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .addEditLineWorkForceCount({ ...action.payload, plan_version: planVersion }, action.factoryId)
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res) => {
                  this.appNotificationUtilClass.presentToast('Edit workforce count successful');
                  return new LLBoardActions.GetLineShiftInfo(
                    action.payload.subjectId,
                    action.factoryId,
                    action.payload.shift_id,
                  );
                }),
                catchError(() => {
                  return of(new LLBoardActions.AddEditLineWorkForceCountFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  resetLineWorkForceCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.ResetLineWorkForceCount),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.ResetLineWorkForceCount, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.ResetLineWorkForceCountFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .resetLineWorkForceCount({ ...action.payload, plan_version: planVersion }, action.factoryId)
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res) => {
                  this.appNotificationUtilClass.presentToast('Reset line workforce count successful');
                  return new LLBoardActions.GetLineShiftInfo(
                    action.payload.subjectId,
                    action.factoryId,
                    action.payload.shift_id,
                  );
                }),
                catchError(() => {
                  return of(new LLBoardActions.ResetLineWorkForceCountFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  getLLTaskManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLLBoardTaskManager),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([, planVersion]: [LLBoardActions.GetLLBoardTaskManager, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.GetLLBoardTaskManagerFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService.getLLTaskManager(planVersion).pipe(
              finalize(() => {
                this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
              }),
              map((res) => new LLBoardActions.GetLLBoardTaskManagerSuccess(res)),
              catchError(() => {
                return of(new LLBoardActions.GetLLBoardTaskManagerFailure());
              }),
            ),
          ),
        );
      }),
    ),
  );

  getLearningCurveStripInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLearningCurveStripeInfo),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.GetLearningCurveStripeInfo, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.GetLearningCurveStripeInfoFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService.getLLLearningCurveInfo(action.stripId, planVersion, action.shiftId).pipe(
              finalize(() => {
                this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
              }),
              map((res) => new LLBoardActions.GetLearningCurveStripeInfoSuccess(res)),
              catchError(() => of(new LLBoardActions.GetLearningCurveStripeInfoFailure())),
            ),
          ),
        );
      }),
    ),
  );

  updateLearningCurveDayCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.UpdateLearningCurveDayCount),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.UpdateLearningCurveDayCount, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.UpdateLearningCurveDayCountFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .incrementLLLearningDayCount(action.stripId, { ...action.payload, plan_version: planVersion })
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res) => {
                  this.appNotificationUtilClass.presentToast('Day count update successful');
                  return new LLBoardActions.GetLearningCurveStripeInfo(action.stripId, action.payload.shift_id);
                }),
                catchError(() => {
                  return of(new LLBoardActions.UpdateLearningCurveDayCountFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  addEditLearningCurveStrip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLearningCurveStrip),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.AddEditLearningCurveStrip, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.AddEditLearningCurveStripFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .addEditLLLearningCurves(action.stripId, { ...action.payload, plan_version: planVersion })
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res) => {
                  return new LLBoardActions.GetLearningCurveStripeInfo(action.stripId, action.payload.shift_id);
                }),
                catchError(() => {
                  return of(new LLBoardActions.AddEditLearningCurveStripFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  deleteLearningCurveStrip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.DeleteLearningCurveStrip),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.DeleteLearningCurveStrip, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.DeleteLearningCurveStripFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService.deleteLLLearningCurveStrip(action.stripId, planVersion, action.shiftId).pipe(
              finalize(() => {
                this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
              }),
              map((res) => {
                return new LLBoardActions.GetLearningCurveStripeInfo(action.stripId, action.shiftId);
              }),
              catchError(() => {
                return of(new LLBoardActions.DeleteLearningCurveStripFailure());
              }),
            ),
          ),
        );
      }),
    ),
  );

  getLearningCurveByFactoryOtl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLearningCurveByFactoryOtl),
      switchMap((action: LLBoardActions.GetLearningCurveByFactoryOtl) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getLLLearningCurveByFactoryOTL(action.factoryId, action.otlId, action.shiftId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LLBoardActions.GetLearningCurveByFactoryOtlSuccess(res);
          }),
          catchError(() => of(new LLBoardActions.GetLearningCurveByFactoryOtlFailure())),
        ),
      ),
    ),
  );

  addEditLearningCurveByFactoryOtl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditLearningCurveByFactoryOtl),
      switchMap((action: LLBoardActions.AddEditLearningCurveByFactoryOtl) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.addEditLLLearningCurveByFactoryOTL(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LLBoardActions.GetLearningCurveByFactoryOtl(
              action.payload.factory_id,
              action.payload.otl_id,
              action.payload.shift_id,
            );
          }),
          catchError(() => {
            return of(new LLBoardActions.AddEditLearningCurveByFactoryOtlFailure());
          }),
        ),
      ),
    ),
  );

  getLineStripListingInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.GetLineStripListing),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.GetLineStripListing, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.GetLineStripListingFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService
              .fetchLineLevelStripListing(action.lineSubject, { ...action.payload, plan_version: planVersion })
              .pipe(
                finalize(() => {
                  this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
                }),
                map((res): LLBoardActions.TLLBoardActions => new LLBoardActions.GetLineStripListingSuccess(res)),
                catchError(() => {
                  return of(new LLBoardActions.GetLineStripListingFailure());
                }),
              ),
          ),
        );
      }),
    ),
  );

  getLineStripPlannedNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.LineStripPlannedNumbers),
      switchMap((action: LLBoardActions.LineStripPlannedNumbers) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getStripPlannedNumbers(action.stripId, action.factoryId, action.shiftId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.LineStripPlannedNumbersSuccess(res)),
          catchError(() => {
            return of(new LLBoardActions.LineStripPlannedNumbersFailure());
          }),
        ),
      ),
    ),
  );
  unloadStripMovePartially$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.UnloadStripMovePartially),
      switchMap((action: LLBoardActions.UnloadStripMovePartially) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.unloadStripMovePartially(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LLBoardActions.UnloadStripMovePartiallySuccess()),
          catchError(() => {
            return of(new LLBoardActions.UnloadStripMovePartiallyFailure());
          }),
        ),
      ),
    ),
  );

  addEditNoteOnStrip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLLBoardInfoActionType.AddEditNoteOnStrip),
      withLatestFrom(this.#selectedPlanVersion$),
      switchMap(([action, planVersion]: [LLBoardActions.AddEditNoteOnStrip, string | null]) => {
        if (!planVersion) return this.#onPlanVersionMissing(new LLBoardActions.AddEditNoteOnStripFailure());
        return from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService.addEditNote({ ...action.payload, plan_version: planVersion }).pipe(
              finalize(() => {
                this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
              }),
              map((): LLBoardActions.TLLBoardActions => new LLBoardActions.AddEditNoteOnStripSuccess()),
              catchError(() => {
                return of(new LLBoardActions.AddEditNoteOnStripFailure());
              }),
            ),
          ),
        );
      }),
    ),
  );
}
